import { Modal } from '../../../components/Modal';
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  FETCH_INTERMEDIATE_MITIGATION,
  UPDATE_INTERMEDIATE_MITIGATION,
} from '../queries';
import { ColumnsType } from 'antd/es/table';
import { InternalIntermediateForm } from './forms/InternalIntermediateForm';
import FinancialData from '../../../components/common/FinancialData';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';
import DependenciesTitle from '../../../components/common/DependenciesTitle';
import { tableSortingMethods } from '../../../utils';
import { GET_LOCATIONS_TO_MITIGATE } from '../queries/locationSelection';
import { useParams, useSearchParams } from 'react-router-dom';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';

type Location = {
  delay: number;
  locationName: string;
  percentage: number;
  uuid: string;
};

type LocationSupported = {
  locationName: string;
  uuid: string;
};

type Division = {
  name: string;
  uuid: string;
};

type Intermediate = {
  uuid: string;
  division: Division;
  locations: Location[];
  locationSupported: LocationSupported;
  location: LocationSupported;
  name: string;
  revenue: number;
};

type IntermediateMitigationOptionDashboard = {
  intermediate: Intermediate;
};

interface DataReturned {
  intermediateMitigationOptionDashboard: IntermediateMitigationOptionDashboard[];
}

interface InternalIntermediateProps {
  division: string;
  location: string;
}

export interface TableData {
  uuid: string;
  locationSupported: string;
  locations: Location[];
  locationSupportedUuid: string;
  revenue: number;
}

export default function InternalIntermediate({
  division,
  location,
}: InternalIntermediateProps) {
  const [item, setItem] = useState<TableData>();
  const [modalOpen, setModalOpen] = useState(false);
  const params = useParams();
  let [searchParams] = useSearchParams();
  const locationId = searchParams.get('location')!;
  const divisionId = params.divisionId!;

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_INTERMEDIATE_MITIGATION,
    {
      variables: {
        division: { uuid: division },
        location: { uuid: location },
        intermediateType: 'Internal',
      },
    },
  );

  const { data: locationsToMitigate, loading: loadingMitigatedLocations } =
    useQuery(GET_LOCATIONS_TO_MITIGATE, {
      variables: {
        data: {
          divisionId: divisionId,
          parentId: locationId,
        },
      },
      skip: !divisionId || !locationId,
    });

  const [UpdateIntermediateMitigation, { loading: updateIntermediateLoading }] =
    useMutation(UPDATE_INTERMEDIATE_MITIGATION, {
      refetchQueries: ['GetIntermediateMitigationMatrix'],
    });

  const intermediatesLocations =
    data?.intermediateMitigationOptionDashboard[0]?.intermediate.locations ||
    [];

  const locationsToMitigateData =
    locationsToMitigate?.getLocationsByDivisionAndParent || [];

  const flattenLocationsToMitigate = useMemo(() => {
    if (!locationsToMitigateData) return [];
    return locationsToMitigateData.map((loc: any) => loc.locationId);
  }, [locationsToMitigateData]);

  const intermediatesFilteredLocations = useMemo(() => {
    if (!intermediatesLocations) return [];
    return intermediatesLocations.filter(
      (loc) => flattenLocationsToMitigate?.includes(loc.uuid),
    );
  }, [intermediatesLocations, flattenLocationsToMitigate]);

  const handleCancel = () => setModalOpen(false);

  const convertData = (data: DataReturned): TableData[] => {
    if (data?.intermediateMitigationOptionDashboard.length === 0) return [];
    return data?.intermediateMitigationOptionDashboard?.map((item) => {
      return {
        uuid: item.intermediate.uuid,
        locationSupported: item.intermediate.locationSupported.locationName,
        locations: item.intermediate.locations,
        locationSupportedUuid: item.intermediate.locationSupported.uuid,
        revenue: item.intermediate.revenue,
      };
    });
  };

  const modifyVariables = ({ currentRecord }: ModifyVariablesArgs) => {
    const commonValues = {
      division: { uuid: division },
      location: {
        uuid: location,
      },
      intermediateType: 'Internal',
      intermediate: { uuid: currentRecord.uuid },
      mutation: UpdateIntermediateMitigation,
    };

    return {
      ...commonValues,
    };
  };

  const locationColumns: any = intermediatesFilteredLocations.map(
    (location, index: number) => ({
      title: <DependenciesTitle name={location.locationName} />,
      dataIndex: 'locations',
      key: location.uuid,
      width: 150,
      align: 'right',
      colIndex: index,
      editable: true,
      sorter: (a: any, b: any) =>
        tableSortingMethods.number(
          a.locations?.find((loc: any) => loc.uuid === location.uuid)
            ?.percentage,
          b.locations?.find((loc: any) => loc.uuid === location.uuid)
            ?.percentage,
        ),
      showSorterTooltip: false,
      render: (_: any, record: any) => {
        return (
          <div className="custom-table--column text-sm">
            <span
              className={`font-semibold ${
                record.locations?.find((loc: any) => loc.uuid === location.uuid)
                  ?.percentage === 0
                  ? 'text-secondary-200'
                  : ''
              }`}
            >
              {
                record.locations?.find((loc: any) => loc.uuid === location.uuid)
                  ?.percentage
              }
              {'%'}
            </span>
            <br />
            <span
              className={`font-semibold ${
                record.locations?.find((loc: any) => loc.uuid === location.uuid)
                  ?.percentage === 0
                  ? 'text-secondary-200'
                  : ''
              }`}
            >
              {
                record.locations?.find((loc: any) => loc.uuid === location.uuid)
                  ?.delay
              }
              {' mths'}
            </span>
          </div>
        );
      },
    }),
  );

  const columns: ColumnsType<TableData> = [
    {
      title: '',
      dataIndex: 'locationSupported',
      key: 'locationSupported',
      width: 220,
      fixed: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationSupported, b.locationSupported),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Revenue Supported Annually',
      dataIndex: 'revenue',
      key: 'revenue',
      width: 110,
      fixed: 'left',
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.revenue, b.revenue),
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    ...locationColumns,
  ];

  const baseColumns: ExportColumn[] = [
    {
      key: 'locationSupported',
      header: 'Location Supported',
      type: 'text',
      width: 40,
    },
    {
      key: 'revenue',
      header: 'Revenue Supported Annually',
      type: 'money',
      width: 40,
    },
  ];

  const mitigationColumns: ExportColumn[] = [];

  if (intermediatesFilteredLocations) {
    intermediatesFilteredLocations.forEach((location, index) => {
      mitigationColumns.push({
        key: location.uuid,
        header: `${location.locationName} - Percentage (%)`,
        type: 'percentage',
        nestedKey: `locations.${index}.percentage`,
        width: 35,
      });
      mitigationColumns.push({
        key: `${location.uuid}_delay`,
        header: `${location.locationName} - Delay (mths)`,
        type: 'number',
        nestedKey: `locations.${index}.delay`,
        width: 35,
      });
    });
  }

  return (
    <div>
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Intermediates Mitigation"
          columns={columns}
          data={data ? convertData(data) : []}
          loading={
            loading || updateIntermediateLoading || loadingMitigatedLocations
          }
          editable
          modifyVariables={modifyVariables}
          customStyles="custom-table--dependency-mapping"
          inlineEditing
          editableOptions={{
            create: false,
            update: true,
            delete: false,
          }}
          isMatrix
          isMitigation
          mitigatedLocations={flattenLocationsToMitigate}
          scroll={{ x: 800, y: 340 }}
          exportExcelColumns={[...baseColumns, ...mitigationColumns]}
        />
      </QueryErrorBoundary>
      <Modal
        title="Internal Mitigation"
        open={modalOpen}
        onClose={handleCancel}
      >
        <InternalIntermediateForm
          data={item}
          division={division}
          location={location}
          onCancel={handleCancel}
        />
      </Modal>
    </div>
  );
}
